import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { emailRegex, isValidPhoneNumber } from "@constants/validation"
import { useUserPageContext } from "@contexts/main/users_page_context"
import { updateUsers } from "@domain/users"
import useControlledForm from "@hooks/use_controlled_form"
import { Box, Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { RoleTypes, User } from "__generated__/graphql"

export type UserFormData = {
  firstName: string
  lastName: string
  email: string
  phone: {
    value: string
    dialCode?: string
    countryCode: string
  }
  role: string
}

export default function UpdateUser({
  isOpen,
  setClose,
  selectedRow
}: {
  isOpen: boolean
  selectedRow?: User
  setClose: () => void
}) {
  const { translate, setUsersData } = useUserPageContext()

  const schema = yup
    .object({
      firstName: yup
        .string()
        .min(2, translate("first_name_must_be_atleast_2_characters"))
        .required(translate("first_name_field_is_required")),
      lastName: yup
        .string()
        .min(2, translate("last_name_must_be_atleast_2_characters"))
        .required(translate("last_name_field_is_required")),
      email: yup
        .string()
        .matches(emailRegex, translate("email_error_invalid_email"))
        .email(translate("email_error_invalid_email"))
        .required(translate("email_required")),
      role: yup.string().required(translate("role_required")),
      phone: yup
        .object()
        .shape({
          value: yup.string().required(translate("phone_number_is_not_valid")),
          dialCode: yup.string(),
          countryCode: yup
            .string()
            .required(translate("phone_number_is_not_valid"))
        })
        .test(
          "phone_number_is_not_valid",
          translate("phone_number_is_not_valid"),
          isValidPhoneNumber
        )
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<UserFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  useEffect(() => {
    if (selectedRow) {
      reset({
        firstName: selectedRow.name as string,
        lastName: selectedRow.surname as string,
        email: selectedRow.email as string,
        phone: {
          value: selectedRow.phone as string,
          countryCode: "tr",
          dialCode: "90"
        },
        role: selectedRow.role as string
      })
    }
  }, [selectedRow])

  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: UserFormData) {
    if (!selectedRow?._id) {
      return
    }

    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await updateUsers({
      _id: selectedRow?._id,
      name: data.firstName,
      surname: data.lastName,
      email: data.email,
      phone: data.phone.value,
      role: data.role as RoleTypes
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    setUsersData?.((prevUsersData) =>
      prevUsersData.map((user) => {
        if (user._id === selectedRow._id) {
          return {
            ...user,
            role: data.role as RoleTypes,
            name: data.firstName,
            surname: data.lastName,
            email: data.email,
            phone: data.phone.value
          }
        }
        return user
      })
    )
    queryClient.invalidateQueries({ queryKey: ["users-data-table"] })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("update_user")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <Box
          sx={{
            gap: 2,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" }
          }}
        >
          <TextInput<UserFormData>
            label={translate("first_name")}
            disabled={loading}
            name="firstName"
            autoComplete="given-name"
            placeholder={translate("first_name")}
            control={control}
            required
          />
          <TextInput<UserFormData>
            label={translate("last_name")}
            disabled={loading}
            name="lastName"
            control={control}
            autoComplete="family-name"
            placeholder={translate("last_name")}
            required
          />
        </Box>
        <TextInput<UserFormData>
          disabled={loading}
          type="email"
          name="email"
          control={control}
          label={translate("email")}
          autoComplete="email"
          placeholder={translate("email")}
        />
        <TextInput<UserFormData>
          type="phone"
          name="phone"
          disabled={loading}
          label={translate("phone")}
          placeholder={translate("phone")}
          control={control}
          required
        />
        <TextInput
          type="select"
          disabled={loading}
          name="role"
          control={control}
          label={translate("role")}
          placeholder={translate("role")}
          selectOptions={[
            { label: "Admin", value: "ADMIN" },
            { label: "User", value: "USER" }
          ]}
        />
      </Stack>
    </DialogProvider>
  )
}
